import { SectionLabel } from '@/components/marketing/v2/SectionLabel';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';

type Accent = 'lime' | 'terracotta' | 'charcoal';

const PILLARS: ReadonlyArray<{
  num: string;
  title: string;
  desc: string;
  accent: Accent;
  bullets: ReadonlyArray<string>;
  hint: string;
}> = [
  {
    num: '01',
    title: 'Vendre',
    desc: 'Caisse rapide, ticket propre, Mobile Money en un tap. Même quand le réseau coupe.',
    accent: 'lime',
    hint: 'Ce que tu fais chaque heure',
    bullets: [
      'Offline-first natif · 0 F perdu',
      'Orange · MTN · Wave intégrés',
      'Impression thermique & ticket SMS',
    ],
  },
  {
    num: '02',
    title: 'Suivre',
    desc: 'Stocks, ventes, pertes, employés. Un seul écran, une seule vérité, temps réel.',
    accent: 'terracotta',
    hint: 'Ce que tu regardes chaque jour',
    bullets: [
      'Stock multi-boutiques live',
      'Rapports journaliers auto',
      'Alertes rupture & seuils intelligents',
    ],
  },
  {
    num: '03',
    title: 'Décider',
    desc: 'Des données qui parlent. Vois ce qui marche, coupe ce qui pèse, scale ce qui rapporte.',
    accent: 'charcoal',
    hint: 'Ce que tu tranches chaque mois',
    bullets: [
      'KPIs par shop & par vendeur',
      'Analyse AI des tendances',
      'Export PDF & Excel en 1 clic',
    ],
  },
];

const ACCENT_BADGE: Record<Accent, string> = {
  lime: 'bg-lime-500 text-charcoal-800',
  terracotta: 'bg-terracotta-500 text-white',
  charcoal: 'bg-charcoal-800 text-lime-400',
};

export default function HomeFeaturePillarsV2() {
  return (
    <section className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid gap-12 md:grid-cols-[0.85fr_1.15fr] md:gap-20">
          {/* LEFT — editorial title column */}
          <div className="md:sticky md:top-28 md:self-start">
            <SectionLabel>Les 3 piliers</SectionLabel>

            <h2 className="mt-6 font-marketing-display text-[clamp(40px,4.6vw,72px)] font-bold leading-[0.96] tracking-[-0.04em] text-charcoal-800">
              Une plateforme.
              <br />
              <span className="underline-lime">Trois résultats</span>
              <br />
              que tu peux mesurer.
            </h2>

            <p className="mt-7 max-w-md font-marketing-body text-[17px] leading-[1.55] text-charcoal-600">
              Pensé pour le terrain : rapide, fiable, mobile. Pas d'écran inutile, pas de jargon.
              Juste ce qui fait vendre plus, perdre moins, et piloter sans te demander « combien
              j'ai vendu aujourd'hui ? ».
            </p>

            <div className="mt-8 hidden md:block">
              <NeobrutalButton as="link" to="/fonctionnalites" variant="secondary" size="md">
                Voir toutes les fonctionnalités
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M5 12h14" />
                  <path d="M13 6l6 6-6 6" />
                </svg>
              </NeobrutalButton>
            </div>

            <div className="mt-10 hidden items-center gap-4 md:flex">
              <span className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
                Vendre → Suivre → Décider
              </span>
              <span className="h-px flex-1 bg-charcoal-200" />
            </div>
          </div>

          {/* RIGHT — 3 cards stacked vertically */}
          <ol className="relative flex flex-col gap-8 md:gap-10">
            {PILLARS.map((p, idx) => {
              const offsetClass =
                idx === 1 ? 'md:translate-x-2' : idx === 2 ? 'md:-translate-x-1' : '';
              return (
                <li key={p.num} className="relative">
                  <article
                    className={`group relative flex flex-col border-2 border-charcoal-800 bg-white p-7 shadow-neobrutal-6 transition-transform duration-200 hover:-translate-x-[3px] hover:-translate-y-[3px] hover:shadow-neobrutal-10 md:p-9 ${offsetClass}`}
                  >
                    <header className="flex items-start gap-5">
                      <div
                        className={`inline-flex h-14 w-14 flex-shrink-0 items-center justify-center border-2 border-charcoal-800 font-marketing-display text-xl font-bold shadow-neobrutal-2 ${ACCENT_BADGE[p.accent]}`}
                      >
                        {p.num}
                      </div>
                      <div className="flex-1">
                        <div className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
                          {p.hint}
                        </div>
                        <h3 className="mt-1 font-marketing-display text-[28px] font-bold leading-[1] tracking-[-0.025em] text-charcoal-800 md:text-[32px]">
                          {p.title}
                        </h3>
                      </div>
                    </header>

                    <p className="mt-5 max-w-2xl font-marketing-body text-[16px] leading-[1.55] text-charcoal-600 md:text-[17px]">
                      {p.desc}
                    </p>

                    <div className="mt-6 border-t-2 border-dashed border-charcoal-200 pt-5">
                      <ul className="flex flex-col gap-2.5 md:flex-row md:flex-wrap md:gap-x-8 md:gap-y-3">
                        {p.bullets.map((b) => (
                          <li
                            key={b}
                            className="flex items-start gap-2.5 font-marketing-body text-[14.5px] text-charcoal-700 md:text-[15px]"
                          >
                            <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 bg-lime-500" />
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Mobile-only CTA below stack */}
        <div className="mt-10 flex justify-center md:hidden">
          <NeobrutalButton as="link" to="/fonctionnalites" variant="secondary" size="md">
            Voir toutes les fonctionnalités
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M5 12h14" />
              <path d="M13 6l6 6-6 6" />
            </svg>
          </NeobrutalButton>
        </div>
      </div>
    </section>
  );
}
